/**
 * Deterministic PRNG.
 *
 * The whole engine is replayable from `(seed, actions[])`, which means every random
 * draw has to come from a cursor we can serialise. We use mulberry32: 32-bit state,
 * one multiply-xor-shift round, good enough distribution for map generation and deck
 * shuffling, and — crucially — the state is a single number we can stash in GameState.
 */

/** Advance a mulberry32 state and return `[nextState, value in [0,1))]`. */
export function nextRandom(state: number): [number, number] {
  // Keep the state in uint32 range; `| 0` after the add keeps V8 on the fast path.
  const s = (state + 0x6d2b79f5) | 0;
  let t = s;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return [s, ((t ^ (t >>> 14)) >>> 0) / 4294967296];
}

/**
 * A mutable cursor over the PRNG stream.
 *
 * The engine holds `rngState` in GameState and wraps it in a Rng for the duration of
 * a mutation, then writes the advanced state back. That keeps the state authoritative
 * in one place instead of scattered closure variables.
 */
export class Rng {
  constructor(public state: number) {}

  /** Float in [0, 1). */
  next(): number {
    const [s, v] = nextRandom(this.state);
    this.state = s;
    return v;
  }

  /** Integer in [0, maxExclusive). Returns 0 when the range is empty. */
  int(maxExclusive: number): number {
    if (maxExclusive <= 0) return 0;
    return Math.floor(this.next() * maxExclusive);
  }

  /** Integer in [min, max] inclusive. */
  range(min: number, max: number): number {
    if (max <= min) return min;
    return min + this.int(max - min + 1);
  }

  /** True with the given probability. */
  chance(p: number): boolean {
    return this.next() < p;
  }

  /** Uniform pick. Throws on an empty array so bugs surface in tests, not in play. */
  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('Rng.pick: empty array');
    return items[this.int(items.length)]!;
  }

  /**
   * Weighted pick. `weight` must return a non-negative number; entries weighing 0 are
   * never chosen. Falls back to the last item if float error leaves a remainder.
   */
  weighted<T>(items: readonly T[], weight: (item: T) => number): T {
    if (items.length === 0) throw new Error('Rng.weighted: empty array');
    let total = 0;
    for (const item of items) total += Math.max(0, weight(item));
    if (total <= 0) return this.pick(items);

    let roll = this.next() * total;
    for (const item of items) {
      roll -= Math.max(0, weight(item));
      if (roll < 0) return item;
    }
    return items[items.length - 1]!;
  }

  /** Fisher-Yates, in place, returning the same array for chaining. */
  shuffle<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i--) {
      const j = this.int(i + 1);
      const tmp = items[i]!;
      items[i] = items[j]!;
      items[j] = tmp;
    }
    return items;
  }

  /** Copy-then-shuffle, for when the source must stay untouched. */
  shuffled<T>(items: readonly T[]): T[] {
    return this.shuffle(items.slice());
  }
}

/**
 * Turn an arbitrary string into a seed. Used so players can type a memorable seed
 * ("BETELGEUSE") and get the same galaxy every time. FNV-1a, 32-bit.
 */
export function seedFromString(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** A short, pronounceable seed word, for showing the current galaxy's name. */
export function seedToWord(seed: number): string {
  const consonants = 'BCDFGHJKLMNPRSTVWXZ';
  const vowels = 'AEIOU';
  const rng = new Rng(seed);
  let word = '';
  for (let i = 0; i < 3; i++) {
    word += consonants[rng.int(consonants.length)];
    word += vowels[rng.int(vowels.length)];
  }
  return word;
}
